import React, { useEffect, Suspense, lazy } from 'react';
import styled from 'styled-components';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { capturePageview } from './analytics';
import { ThemeProvider } from './components/theme/ThemeContext';
import { SoundProvider } from './components/sound/SoundContext';
import { LocaleProvider } from './i18n/LocaleContext';
import { isLocale, localizedPath, stripLocale, saveLocale } from './config/locale';
import { applyCanvasPreset } from './config/canvas';
import { applyTypePreset } from './config/typeface';
import { getBackgroundPreset } from './config/background';
import { SHOW_CZS_CASE_STUDY } from './config/czsCaseStudy';
// @ts-ignore
import SiteEmbroidery from './components/background/SiteEmbroidery.tsx';
// Type scale (sizes/weights/tracking) loads before the family layer so the
// !important font-family assignments in typography.scss always win.
import './styles/typescale.scss';
import './styles/typography.scss';
import './styles/decorations.scss';


// Header and Footer are on the critical path, so they stay eager.
// @ts-ignore
import Header from './components/header/header.tsx';
// @ts-ignore
import Footer from './components/footer/footer.tsx';
// Home pulls in the hero shaders and the project data, so it is route-split too.
// @ts-ignore
const Home = lazy(() => import('./components/home/Home.tsx'));

// Every sub-page is route-split so it is not in the homepage bundle. This is the
// main lever against the single ~1MB main chunk the perf audit flagged.
// @ts-ignore
const BlogListingPage = lazy(() => import('./components/blog/BlogListingPage.tsx'));
// @ts-ignore
const BlogPostPage = lazy(() => import('./components/blog/BlogPostPage.tsx'));
// @ts-ignore
const NowPage = lazy(() => import('./components/now/NowPage.tsx'));
// @ts-ignore
const ServicesPage = lazy(() => import('./components/services/ServicesPage.tsx'));
// Unlisted proposal page: reachable only by direct link, noindex, not in nav or prerender.
// @ts-ignore
const AiEmployeePage = lazy(() => import('./components/services/AiEmployeePage.tsx'));
// @ts-ignore
const ThingsPage = lazy(() => import('./components/things/ThingsPage.tsx'));
// @ts-ignore
const SharePreviewPage = lazy(() => import('./components/share/SharePreviewPage.tsx'));
// @ts-ignore
const StyleguidePage = lazy(() => import('./components/styleguide/StyleguidePage.tsx'));

// @ts-ignore
const CzsChatbotPage = lazy(() => import('./components/projects/CzsChatbotPage.tsx'));

// @ts-ignore
const InzerproCaseStudyPage = lazy(() => import('./components/projects/InzerproCaseStudyPage.tsx'));

// @ts-ignore
const ReferencesPage = lazy(() => import('./components/references/ReferencesPage.tsx'));
// @ts-ignore
const NotFound = lazy(() => import('./components/notfound/NotFound.tsx'));

// Dev-only inline text editor. The dynamic require + NODE_ENV guard keeps it out
// of the production bundle entirely (see src/devEditor/ and src/setupProxy.js).
const EditOverlay =
  process.env.NODE_ENV === 'development'
    ? (require('./devEditor/EditOverlay').default as React.FC)
    : null;

// The page color itself lives on <body> (see index.css), theme-driven via
// `data-theme` on <html>. The container stays transparent so the fixed
// SiteEmbroidery layer (z-index -1) can sit between the body color and the
// content.
const AppContainer = styled.div`
  min-height: 100vh;
  padding: 0;
  margin: 0;
`;

// SPA route changes need a manual PostHog $pageview (init disables auto-capture).
const PostHogPageview: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    capturePageview();
  }, [location]);
  return null;
};

// Reset scroll to the top whenever the path changes, so opening a post (or any
// page) always starts at the top instead of inheriting the previous scroll.
const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Honour in-page anchors (e.g. /#projects from a blog link): scroll to the
    // target — retrying briefly while the destination section mounts — instead
    // of jumping to the top.
    if (hash) {
      let tries = 0;
      const tick = () => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView();
        else if (tries++ < 25) setTimeout(tick, 40);
      };
      tick();
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

// Back-compat for the old query-param locale scheme. ?lang=sk on any URL
// redirects (replace) to the /sk path equivalent so old shared links keep
// working; ?lang=reset clears the remembered preference. Mounted inside Router.
const LegacyLangRedirect: React.FC = () => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(search);
    const lang = params.get('lang');
    if (!lang) return;
    if (lang === 'reset') {
      try { localStorage.removeItem('site-locale'); } catch { /* private mode */ }
      params.delete('lang');
      const q = params.toString();
      navigate(pathname + (q ? `?${q}` : '') + hash, { replace: true });
      return;
    }
    if (isLocale(lang)) {
      saveLocale(lang);
      params.delete('lang');
      const q = params.toString();
      navigate(localizedPath(stripLocale(pathname), lang) + (q ? `?${q}` : '') + hash, { replace: true });
    }
  }, [pathname, search, hash, navigate]);
  return null;
};

// One shell for every route: the shared Header (with the single site-wide theme
// toggle) and Footer wrap the page. Theme is owned by ThemeProvider and read via
// context, so the header, footer, home, and sub-pages all stay in sync.
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AppContainer>
    <a className="skip-link" href="#main-content">Skip to content</a>
    {getBackgroundPreset() === 'embroidery' && <SiteEmbroidery />}
    <Header />
    <main id="main-content">{children}</main>
    <Footer />
  </AppContainer>
);

// The page routes as RELATIVE children, reused under each locale branch (/, /sk,
// /cs). English keeps its exact root URLs; the /sk and /cs branches render the
// same pages with the locale derived from the path prefix. A function (not a
// shared element) so each branch gets its own fresh route descriptors.
const pageRoutes = () => (
  <>
    <Route index element={<Shell><Home /></Shell>} />
    <Route path="blog" element={<Shell><BlogListingPage /></Shell>} />
    <Route path="blog/:slug" element={<Shell><BlogPostPage /></Shell>} />
    <Route path="now" element={<Shell><NowPage /></Shell>} />
    <Route path="services" element={<Shell><ServicesPage /></Shell>} />
    <Route path="services/ai-employee" element={<Shell><AiEmployeePage /></Shell>} />
    <Route path="things" element={<Shell><ThingsPage /></Shell>} />
    {SHOW_CZS_CASE_STUDY && <Route path="projects/czs-muni-chatbot" element={<Shell><CzsChatbotPage /></Shell>} />}
    <Route path="projects/inzerpro" element={<Shell><InzerproCaseStudyPage /></Shell>} />
    <Route path="references" element={<Shell><ReferencesPage /></Shell>} />
    <Route path="share-preview" element={<Shell><SharePreviewPage /></Shell>} />
    <Route path="styleguide" element={<Shell><StyleguidePage /></Shell>} />
    <Route path="*" element={<Shell><NotFound /></Shell>} />
  </>
);

const App: React.FC = () => {
  // Resolve the ?canvas= and ?type= previews (remembered presets) once on mount.
  useEffect(() => { applyCanvasPreset(); applyTypePreset(); }, []);
  return (
    <HelmetProvider>
      <ThemeProvider>
        <SoundProvider>
        <Router>
          {/* LocaleProvider is INSIDE Router so it can derive locale from the path. */}
          <LocaleProvider>
            <ScrollToTop />
            <LegacyLangRedirect />
            <PostHogPageview />
            <Suspense fallback={null}>
              <Routes>
                <Route path="/">{pageRoutes()}</Route>
                <Route path="/sk">{pageRoutes()}</Route>
                <Route path="/cs">{pageRoutes()}</Route>
              </Routes>
            </Suspense>
            {EditOverlay && <EditOverlay />}
          </LocaleProvider>
        </Router>
        </SoundProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
